import { z } from "zod";
import { FOCUS_IDS } from "@/features/payroll/payroll-run-focus";
import { WIZARD_STEP_IDS } from "@/features/payroll/payroll-run-steps";

/**
 * URL search params for `/$workspaceSlug/payroll/runs/$runId`.
 *
 * `step` is a READING POSITION, not the wizard's state. The run's status is
 * the state machine (FR-68), and `resolveRunWizardStep` clamps whatever the
 * URL says to what that status allows, so a link to `?step=pay` on a draft run
 * opens the step the run is actually at. It lives here rather than in
 * `useState` so "look at the resolve step of this run" is a link somebody can
 * send, and so the browser Back button steps between panels (S3).
 *
 * `paymentDate` is the transfer date the admin agreed with their bank. In the
 * URL for the same reason: an afternoon of payroll should survive a refresh,
 * and re-picking a date somebody already agreed with a bank is how a file goes
 * out with the wrong one. A date is not PII, so FE-SEC7 is satisfied.
 */
/**
 * Deliberately duplicated from `salary-slip.schema.ts` rather than imported.
 *
 * `router.tsx` eagerly imports every route's `validateSearch`, so whatever
 * this file imports lands in the INITIAL chunk. The initial bundle is within
 * a few KB of its 150 KB budget (P1), and four string literals are cheaper
 * than dragging a schema module across that line. `payroll-run-steps.ts`
 * exists for exactly the same reason.
 */
const SLIP_STATUS_VALUES = ["draft", "submitted", "paid", "cancelled"] as const;
const MEMBERSHIP_ID = /^[A-Za-z0-9][A-Za-z0-9_-]{2,63}$/;

export const payrollRunDetailRouteSearchSchema = z.object({
  outcomePage: z.coerce.number().int().min(0).optional().catch(undefined),
  step: z.enum(WIZARD_STEP_IDS).optional().catch(undefined),
  /// Which half of the page is on screen. In the URL so "look at this run's
  /// payslips" is a link, and so Back leaves the tab rather than the page.
  tab: z.enum(["wizard", "slips"]).optional().catch(undefined),
  slipStatus: z.enum(SLIP_STATUS_VALUES).optional().catch(undefined),
  slipPeriodStart: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
  // The member-name search is NOT here. S3 wants filters in the URL, but a
  // person's name is PII and a URL reaches browser history, server logs and
  // every analytics tool (FE-SEC7). Status and a date are neither, so they
  // stay shareable; the name search lives in component state instead.
  paymentDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
  /// Dashboard tile. Entity ids only (FE-SEC7). Stale `extras` / `recoveries`
  /// still parse so a sent link opens This month rather than the default.
  focus: z.enum(FOCUS_IDS).optional().catch(undefined),
  attendanceTab: z.enum(["attendance", "leave"]).optional().catch(undefined),
  attendanceMemberId: z.string().min(1).optional().catch(undefined),
  attendanceIssue: z.string().min(1).optional().catch(undefined),
  attendanceDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
  /// PF-006 and in-run assign deep links. Entity id only (FE-SEC7), never a
  /// name. Dropped after the salary dialog opens so Back does not re-open it.
  membershipId: z.string().regex(MEMBERSHIP_ID).optional().catch(undefined),
  /// Set when the admin has already sent the transfer and wants Mark paid
  /// without a stored payment file. Not PII. Keep the number so the URL
  /// stays `?payReady=1` after validate, not a quoted string.
  payReady: z
    .union([z.literal("1"), z.literal(1)])
    .transform(() => 1 as const)
    .optional()
    .catch(undefined),
});
// Not .strict(): parent-merged search (firstRun, leftover keys) must not
// throw. Unknown keys are stripped. membershipId has to survive PF-006 links.

export type PayrollRunDetailRouteSearch = z.infer<typeof payrollRunDetailRouteSearchSchema>;

export function parseRunMembershipId(value: unknown): string | undefined {
  if (typeof value !== "string" || !MEMBERSHIP_ID.test(value)) return undefined;
  return value;
}

export function parsePayrollRunDetailSearch(
  search: Record<string, unknown>,
): PayrollRunDetailRouteSearch {
  const parsed = payrollRunDetailRouteSearchSchema.parse(search);
  const membershipId = parsed.membershipId ?? parseRunMembershipId(search.membershipId);
  return membershipId === undefined ? parsed : { ...parsed, membershipId };
}

/// Write the number so TanStack serializes `?payReady=1`, not a quoted string.
export const PAY_READY_SEARCH_WRITE = 1 as const;
